// NotFound.js — iMatrix Technologies 404 Page (Interactive Terminal)
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './NotFound.css';

const COMMANDS = {
  help: {
    output: [
      '┌────────────────────────────────────────────────────────────────────────────┐',
      '│                         SYSTEM DIAGNOSTIC HELP PANEL                       │',
      '├────────────────────────────────────────────────────────────────────────────┤',
      '│  [Navigation Commands]                                                     │',
      '│  home         → Returns to the primary homepage                            │',
      '│  about        → Learn about iMatrix Technologies legacy & leadership       │',
      '│  services     → Browse Tally solutions, infrastructure & custom software   │',
      '│  contact      → Instantly consult an expert or open direct channels        │',
      '│  careers      → View modern job opportunities at iMatrix                   │',
      '│                                                                            │',
      '│  [Utility Commands]                                                        │',
      '│  who          → Displays company identity & official contact records       │',
      '│  status       → System check of critical network endpoints & integrations  │',
      '│  clear        → Wipes current console buffer clear                         │',
      '│                                                                            │',
      '│  [Hint] Type a command below and hit ENTER. Use Arrow Up/Down for history. │',
      '└────────────────────────────────────────────────────────────────────────────┘',
    ],
  },
  home:     { redirect: '/', output: ['⚡ Redirection protocol initialized. Navigating to homepage...'] },
  contact:  { redirect: '/contact', output: ['📞 Initializing contact portal. Redirecting...'] },
  services: { redirect: '/ServicePage', output: ['⚙️ Fetching system portfolio. Redirecting to services...'] },
  about:    { redirect: '/AboutPage', output: ['📖 Loading corporate chronicles. Redirecting to about us...'] },
  careers:  { redirect: '/career_pages', output: ['💼 Accessing talent acquisition portal. Redirecting...'] },
  who: {
    output: [
      '',
      '  ================= COPORATE IDENTITY PROFILE =================',
      '  🏢 Brand Name      : iMatrix Technologies Private Limited',
      '  📅 Founded         : 2006 (Formal private incorporation in 2013)',
      '  🏅 Authorized Tier : Authorized Tally 5-Star Partner',
      '  📦 Core Solutions  : Tally, Infrastructure, Security, Custom Apps',
      '  📍 Headquarters    : MMDA Colony, Arumbakkam, Chennai, TN, India',
      '  🌐 Web Address     : https://www.imatrixtechnologies.com',
      '  📞 Contact Line    : +91 98416 09990 | admin@imatrixtechnologies.com',
      '  =============================================================',
      '',
    ],
  },
  status: {
    output: [
      '',
      '  ================= SYSTEM INTEGRITY METRICS =================',
      '  📡 Connection   : ONLINE (HTTP/2 SSL Securify Active)',
      '  🚀 Performance  : OPTIMAL (SPA React virtual DOM operational)',
      '  🔒 Auth Shield  : ENFORCED (Tally 5-Star authorized certification)',
      '  📦 Integrations : Geolocation APIs online | Sheets sync online',
      '  📊 Host Latency : 18ms (Stable route to server cloud)',
      '  ============================================================',
      '',
    ],
  },
  clear: { clear: true, output: [] },
  konami: {
    output: [
      '',
      '  ███████╗ █████╗ ███████╗████████╗███████╗██████╗ ',
      '  ██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔════╝██╔══██╗',
      '  █████╗  ███████║███████╗   ██║   █████╗  ██████╔╝',
      '  ██╔══╝  ██╔══██║╚════██║   ██║   ██╔══╝  ██╔══██╗',
      '  ███████╗██║  ██║███████║   ██║   ███████╗██║  ██║',
      '  ╚══════╝╚═╝  ╚═╝╚══════╝   ╚═╝   ╚══════╝╚═╝  ╚═╝',
      '',
      '  🎉 Congratulations, hacker! You unlocked the terminal secret.',
      '  Psst... try typing ↑ ↑ ↓ ↓ ← → ← → B A on any site page too 👀',
      '',
    ],
  },
};

const INITIAL_OUTPUT = [
  '┌────────────────────────────────────────────────────────────────────────────┐',
  '│                 iMatrix Technologies — Diagnostics Terminal                │',
  '│                      SYSTEM ONLINE · ESTABLISHED 2006                      │',
  '└────────────────────────────────────────────────────────────────────────────┘',
  '',
  '  ⚠️  ERROR 404: RESOURCE NOT LOCATED',
  '  The file/page directory you requested is temporarily unavailable or deleted.',
  '',
  '  💡 NEED ASSISTANCE? ',
  '     Type "help" and press ENTER to explore quick solutions and redirect links.',
  '',
];

const NotFound = () => {
  const navigate = useNavigate();
  const [input, setInput]     = useState('');
  const [lines, setLines]     = useState(INITIAL_OUTPUT);
  const [history, setHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [glitch, setGlitch]   = useState(false);
  const outputRef             = useRef(null);
  const inputRef              = useRef(null);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [lines]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const scheduleGlitch = () => {
      const delay = 3500 + Math.random() * 4500;
      return setTimeout(() => {
        setGlitch(true);
        setTimeout(() => { setGlitch(false); scheduleGlitch(); }, 500);
      }, delay);
    };
    const t = scheduleGlitch();
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const cmd = input.trim().toLowerCase();
    if (!cmd) return;
    const newHistory = [cmd, ...history];
    setHistory(newHistory);
    setHistIdx(-1);
    setInput('');
    const prompt = `> ${cmd}`;
    const command = COMMANDS[cmd];
    if (command) {
      if (command.clear) {
        setLines([...INITIAL_OUTPUT]);
      } else {
        setLines(prev => [...prev, prompt, ...(command.output || []), '']);
        if (command.redirect) {
          setTimeout(() => navigate(command.redirect), 900);
        }
      }
    } else {
      setLines(prev => [
        ...prev,
        prompt,
        `  \u2717  Command not found: "${cmd}"`,
        `  Tip: Type "help" to see available commands.`,
        '',
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIdx = Math.min(histIdx + 1, history.length - 1);
      setHistIdx(newIdx);
      setInput(history[newIdx] || '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIdx = Math.max(histIdx - 1, -1);
      setHistIdx(newIdx);
      setInput(newIdx === -1 ? '' : history[newIdx]);
    }
  };

  return (
    <div className="nf-page" onClick={() => inputRef.current?.focus()}>
      <div className="nf-bg" aria-hidden="true" />
      <div className="nf-scanlines" aria-hidden="true" />

      <div className="nf-container">
        <div className={`nf-error-number ${glitch ? 'nf-glitch' : ''}`} aria-hidden="true">
          <span data-text="404">404</span>
        </div>
        <p className="nf-tagline">Lost in the matrix? Let&apos;s get you back.</p>

        <div className="nf-terminal">
          <div className="nf-terminal-bar">
            <span className="nf-dot nf-dot--red" />
            <span className="nf-dot nf-dot--yellow" />
            <span className="nf-dot nf-dot--green" />
            <span className="nf-terminal-title">imatrix-terminal — bash</span>
          </div>

          <div className="nf-output" ref={outputRef}>
            {lines.map((line, i) => (
              <div key={i} className="nf-output-line">{line || '\u00a0'}</div>
            ))}
          </div>

          <form className="nf-input-row" onSubmit={handleSubmit}>
            <span className="nf-prompt">imatrix@404:~$</span>
            <input
              ref={inputRef}
              className="nf-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              aria-label="Terminal input"
            />
          </form>
        </div>

        <p className="nf-hint">Type <kbd>help</kbd> to see available commands &middot; Use &uarr;&darr; for history</p>
      </div>
    </div>
  );
};

export default NotFound;
